import { useState, useEffect, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  FaCalendarAlt,
  FaShoppingCart,
  FaClipboardList,
  FaUserTie,
  FaChartLine,
  FaMoneyBillWave,
  FaBell,
  FaRunning,
  FaMedal,
  FaHeartbeat,
  FaChevronRight,
  FaFire,
  FaWater,
  FaLeaf,
  FaCloud,
  FaCheckCircle,
  FaBriefcase,
  FaToggleOn,
  FaToggleOff,
  FaClock,
  FaExclamationTriangle,
  FaInbox,
  FaTimes,
  FaStore,
  FaMagic
} from 'react-icons/fa';
import { RiMedalLine } from 'react-icons/ri';
import yogBg from '../../images/yg1.jpg';
import UserContext from '../../Components/UserContext';
import HttpInterceptor from '../../lib/HttpInterceptor';
import BecomeBusinessPartner from './BecomeBusinessPartner';
import { toast } from 'react-toastify';
import { safeToNumber, formatName } from '../../utils/dataTransformers';

function HomeStud() {

  const { session, setSession } = useContext(UserContext);
  const navigate = useNavigate();
  const [showBecomePartner, setShowBecomePartner] = useState(false);
  const [dashboardView, setDashboardView] = useState('member'); // 'member' or 'professional'
  const [isPWA, setIsPWA] = useState(false);
  const [showSwitchConfirm, setShowSwitchConfirm] = useState(false);
  const [isAiAgentsEnabled, setIsAiAgentsEnabled] = useState(true);

  const [stats, setStats] = useState({
    classesAttended: 0,
    coursesEnrolled: 0,
    progressAchieved: 0,
    upcomingClasses: 0,
    pendingPayments: 0,
    trainerSessions: 0,
    streakDays: 0,
    caloriesBurned: 0
  });

  const [isLoading, setIsLoading] = useState(true);
  const [activities, setActivities] = useState([]);
  const [isLoadingActivities, setIsLoadingActivities] = useState(true);
  const [activitiesError, setActivitiesError] = useState(null);
  const [learningJourneyStatus, setLearningJourneyStatus] = useState({
    hasEnrolledPrograms: false,
    hasAttendedClasses: false,
    hasTrackedProgress: false,
    hasAchievedGoals: false
  });
  const [isLoadingJourney, setIsLoadingJourney] = useState(true);

  // Fetch student stats from backend
  const fetchStats = async () => {
    try {
      setIsLoading(true);
      // Disable error toast - we'll handle errors gracefully without spamming the user
      const response = await HttpInterceptor.get('/api/students/dashboard/stats', {
        showErrorToast: false
      });
      if (response.status === 200 && response.data) {
        // Handle different response formats
        const statsData = response.data?.data || response.data || {};
        setStats({
          classesAttended: safeToNumber(statsData.classesAttended || statsData.classes_attended, 0),
          coursesEnrolled: safeToNumber(statsData.coursesEnrolled || statsData.courses_enrolled, 0),
          progressAchieved: safeToNumber(statsData.progressAchieved || statsData.progress_achieved, 0),
          upcomingClasses: safeToNumber(statsData.upcomingClasses || statsData.upcoming_classes, 0),
          pendingPayments: safeToNumber(statsData.pendingPayments || statsData.pending_payments, 0),
          trainerSessions: safeToNumber(statsData.trainerSessions || statsData.trainer_sessions, 0),
          streakDays: safeToNumber(statsData.streakDays || statsData.streak_days, 0),
          caloriesBurned: safeToNumber(statsData.caloriesBurned || statsData.calories_burned, 0)
        });
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
      // Fallback to default stats if API fails
      setStats({
        classesAttended: 0,
        coursesEnrolled: 0,
        progressAchieved: 0,
        upcomingClasses: 0,
        pendingPayments: 0,
        trainerSessions: 0,
        streakDays: 0,
        caloriesBurned: 0
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch activities from classes and plans
  const fetchActivities = async () => {
    try {
      setActivitiesError(null);
      setIsLoadingActivities(true);

      // Fetch classes to build activities
      // Note: Trainers who are enrolled as students can access this
      const classesResponse = await HttpInterceptor.get('/api/students/classes', {
        showErrorToast: false
      });
      const classes = Array.isArray(classesResponse.data) ? classesResponse.data : (classesResponse.data?.data || []);

      // Fetch plans to get payment activities
      const plansResponse = await HttpInterceptor.get('/api/students/plans', {
        showErrorToast: false
      });
      const plans = Array.isArray(plansResponse.data) ? plansResponse.data : (plansResponse.data?.data || []);

      // Build activities from classes and plans
      const activitiesList = [];

      // Ensure classes is an array before using slice
      const classesArray = Array.isArray(classes) ? classes : [];

      // Add class activities (recent classes)
      classesArray.slice(0, 5).forEach((classItem) => {
        activitiesList.push({
          type: 'class',
          name: classItem.name || 'Class',
          time: classItem.nextSession || 'Recently',
          status: 'completed',
          icon: <FaCalendarAlt className="text-blue-500" />,
          points: 50
        });
      });

      // Ensure plans is an array before filtering
      const plansArray = Array.isArray(plans) ? plans : [];

      // Add payment activities from plans
      plansArray.filter(p => p.paymentStatus === 'paid').slice(0, 2).forEach((plan) => {
        activitiesList.push({
          type: 'payment',
          amount: plan.price || 0,
          time: 'Recently',
          status: 'processed',
          icon: <FaMoneyBillWave className="text-green-500" />,
          points: 0
        });
      });

      // Sort by time (most recent first) and limit to 5
      setActivities(activitiesList.slice(0, 5));
    } catch (err) {
      console.error('Error fetching activities:', err);
      setActivitiesError('Unable to load recent activities');
      setActivities([]);
    } finally {
      setIsLoadingActivities(false);
    }
  };

  // Fetch learning journey status
  const fetchLearningJourneyStatus = async () => {
    try {
      setIsLoadingJourney(true);
      const response = await HttpInterceptor.get('/api/students/learning-journey-status', {
        showErrorToast: false
      });
      if (response.status === 200) {
        const data = response.data?.data || response.data || {};
        setLearningJourneyStatus({
          hasEnrolledPrograms: data.hasEnrolledPrograms || false,
          hasAttendedClasses: data.hasAttendedClasses || false,
          hasTrackedProgress: data.hasTrackedProgress || false,
          hasAchievedGoals: data.hasAchievedGoals || false
        });
      }
    } catch (err) {
      console.error('Error fetching learning journey status:', err);
      // On error, set all to false (not completed)
      setLearningJourneyStatus({
        hasEnrolledPrograms: false,
        hasAttendedClasses: false,
        hasTrackedProgress: false,
        hasAchievedGoals: false
      });
    } finally {
      setIsLoadingJourney(false);
    }
  };

  useEffect(() => {
    // Fetch student data - trainers who are also students can access this
    // The backend will handle authorization (trainers enrolled as students can access)
    fetchStats();
    fetchActivities();
    fetchLearningJourneyStatus();

    // Fetch global settings for AI Agents
    HttpInterceptor.get('/api/admin/public/settings')
      .then(res => {
        if (res.data?.data) {
          setIsAiAgentsEnabled(res.data.data.isAiAgentsEnabled);
        }
      })
      .catch(err => console.error('Error fetching global settings:', err));
  }, []);

  // Refresh session on mount to get latest trainer status
  useEffect(() => {
    let isCancelled = false;

    const refreshSession = async () => {
      try {
        const response = await HttpInterceptor.get('/auth/session');
        if (!isCancelled && response.data && setSession) {
          setSession(response.data);
        }
      } catch (error) {
        if (!isCancelled) {
          console.error('Error refreshing session:', error);
        }
      }
    };

    refreshSession();

    return () => {
      isCancelled = true;
    };
  }, [setSession]);

  // Detect PWA mode
  useEffect(() => {
    const checkPWA = () => {
      const standalone = window.matchMedia('(display-mode: standalone)').matches ||
        window.navigator.standalone === true;
      setIsPWA(standalone);
    };
    checkPWA();
    const mediaQuery = window.matchMedia('(display-mode: standalone)');
    const handleChange = () => checkPWA();
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);


  // Navigation items for PWA mode (replaces quick actions)
  const navigationItems = [
    {
      title: 'Marketplace',
      icon: <FaStore className="text-teal-600" />,
      link: '/student/market',
      bgColor: 'bg-gradient-to-br from-teal-50 to-blue-100/50',
      textColor: 'text-teal-700',
      iconBg: 'bg-teal-100'
    },
    {
      title: 'Services',
      icon: <FaShoppingCart className="text-blue-600" />,
      link: '/student/services',
      bgColor: 'bg-gradient-to-br from-blue-50 to-indigo-100/50',
      textColor: 'text-blue-700',
      iconBg: 'bg-blue-100'
    },
    {
      title: 'My Sessions',
      icon: <FaCalendarAlt className="text-indigo-600" />,
      link: '/student/myclasses',
      bgColor: 'bg-gradient-to-br from-indigo-50 to-teal-100/50',
      textColor: 'text-indigo-700',
      iconBg: 'bg-indigo-100'
    },
    {
      title: 'My Plans',
      icon: <FaClipboardList className="text-teal-600" />,
      link: '/student/plans',
      bgColor: 'bg-gradient-to-br from-teal-50 to-teal-100/50',
      textColor: 'text-teal-700',
      iconBg: 'bg-teal-100'
    },
    {
      title: 'My Instructor',
      icon: <FaUserTie className="text-blue-600" />,
      link: '/student/trainer',
      bgColor: 'bg-gradient-to-br from-indigo-50 to-teal-100/50',
      textColor: 'text-blue-700',
      iconBg: 'bg-indigo-100'
    }
  ];

  if (isAiAgentsEnabled) {
    navigationItems.push({
      title: 'My AI Consultants',
      icon: <FaMagic className="text-purple-600" />,
      link: '/student/my-ai',
      bgColor: 'bg-gradient-to-br from-purple-50 to-indigo-100/50',
      textColor: 'text-purple-700',
      iconBg: 'bg-purple-100'
    });
  }

  // Quick actions - for non-PWA mode only
  const quickActions = [
    {
      title: 'Book Class',
      description: 'Schedule your next session',
      icon: <FaCalendarAlt className="text-blue-500" />,
      link: '/student/myclasses',
      bgColor: 'bg-blue-50',
      textColor: 'text-blue-600'
    },
    {
      title: 'Buy Courses',
      description: 'Explore new programs',
      icon: <FaShoppingCart className="text-green-500" />,
      link: '/student/services',
      bgColor: 'bg-green-50',
      textColor: 'text-green-600'
    },
    {
      title: 'My Plans',
      description: 'Track your progress',
      icon: <FaClipboardList className="text-purple-500" />,
      link: '/student/plans',
      bgColor: 'bg-purple-50',
      textColor: 'text-purple-600'
    },
    {
      title: 'My Trainer',
      description: 'Connect with trainers',
      icon: <FaUserTie className="text-orange-500" />,
      link: '/student/trainer',
      bgColor: 'bg-teal-50',
      textColor: 'text-teal-600'
    }
  ];

  if (isAiAgentsEnabled) {
    quickActions.push({
      title: 'My AI Consultants',
      description: 'Get instant expert advice',
      icon: <FaMagic className="text-purple-500" />,
      link: '/student/my-ai',
      bgColor: 'bg-purple-50',
      textColor: 'text-purple-600'
    });
  }

  // Wellness tips
  const wellnessTips = [
    {
      title: 'Stay Hydrated',
      description: 'Drink 8 glasses of water daily for optimal performance',
      icon: <FaWater className="text-blue-400" />,
      color: 'bg-blue-50'
    },
    {
      title: 'Mindful Breathing',
      description: 'Practice deep breathing exercises for stress relief',
      icon: <FaLeaf className="text-green-400" />,
      color: 'bg-green-50'
    },
    {
      title: 'Consistent Practice',
      description: 'Even 15 minutes daily can transform your practice',
      icon: <FaRunning className="text-purple-400" />,
      color: 'bg-purple-50'
    }
  ];

  if (isLoading || !session) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div
            className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto"
            role="status"
            aria-live="polite"
            aria-label="Loading dashboard"
          >
            <span className="sr-only">Loading your dashboard...</span>
          </div>
          <p className="mt-4 text-gray-600" aria-live="polite">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="relative z-0 overflow-visible">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${yogBg})` }}
        />
        <div className={`relative z-10 ${isPWA ? '' : 'bg-gradient-to-r from-black/70 to-black/50'} ${isPWA ? 'py-8 sm:py-10 pb-12 sm:pb-14' : 'py-24 pb-32'}`}>
          {isPWA && (
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-teal-400 via-blue-400 to-indigo-400"></div>
          )}
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            {stats.streakDays > 0 && (
              <div className="flex justify-center items-center mb-3 sm:mb-4">
                <div
                  className={`${isPWA ? 'bg-gradient-to-r from-teal-400 to-blue-400 text-teal-900 shadow-lg ring-2 ring-teal-300/50' : 'bg-yellow-400/90 text-indigo-900'} ${isPWA ? 'px-3 py-1 text-xs' : 'px-4 py-1 text-sm'} rounded-full font-semibold flex items-center`}
                  role="status"
                  aria-label={`${stats.streakDays}-day practice streak`}
                >
                  <FaFire className={isPWA ? 'mr-1' : 'mr-1'} aria-hidden="true" />
                  <span>{stats.streakDays}-day streak</span>
                </div>
              </div>
            )}
            <h1 className={`${isPWA ? 'text-xl sm:text-2xl' : 'text-4xl md:text-5xl'} font-bold ${isPWA ? 'text-gray-900 drop-shadow-[0_2px_4px_rgba(255,255,255,0.8)]' : 'text-white'} ${isPWA ? 'mb-3' : 'mb-4'}`}>
              {isPWA ? (
                <>
                  <span className="text-teal-700 drop-shadow-[0_2px_4px_rgba(255,255,255,0.9)]">{formatName(session?.name) || 'Member'}</span>
                  <span className="block text-sm text-teal-600 drop-shadow-[0_1px_3px_rgba(255,255,255,0.8)] mt-1 font-normal">Member Dashboard</span>
                </>
              ) : (
                <>Hi! Welcome back, <span className="text-indigo-300">{formatName(session?.name) || 'Member'}</span></>
              )}
            </h1>
            {!isPWA && (
              <p className="text-xl text-gray-200 max-w-3xl mx-auto mb-6">
                Your personal dashboard for tracking progress, managing classes, and achieving your fitness goals.
              </p>
            )}
            <div className={`flex justify-center ${isPWA ? 'gap-2 flex-wrap' : 'gap-4'}`}>
              <div className={`${isPWA ? 'bg-white/95 backdrop-blur-sm border-2 border-teal-300/50 shadow-xl' : 'bg-white/10 backdrop-blur-sm'} ${isPWA ? 'px-3 py-1.5 text-xs' : 'px-4 py-2'} rounded-lg flex items-center`}>
                <FaCalendarAlt className={`${isPWA ? 'text-teal-600' : 'text-blue-400'} ${isPWA ? 'mr-1.5 text-sm' : 'mr-2'}`} />
                <span className={`${isPWA ? 'text-teal-800 font-semibold' : 'text-white'} whitespace-nowrap font-medium`}>{stats.classesAttended} classes</span>
              </div>
              <div className={`${isPWA ? 'bg-white/95 backdrop-blur-sm border-2 border-indigo-300/50 shadow-xl' : 'bg-white/10 backdrop-blur-sm'} ${isPWA ? 'px-3 py-1.5 text-xs' : 'px-4 py-2'} rounded-lg flex items-center`}>
                <RiMedalLine className={`${isPWA ? 'text-indigo-600' : 'text-indigo-300'} ${isPWA ? 'mr-1.5 text-sm' : 'mr-2'}`} />
                <span className={`${isPWA ? 'text-indigo-800 font-semibold' : 'text-white'} whitespace-nowrap font-medium`}>Level {Math.floor(stats.progressAchieved / 20) + 1}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Business Partner Section */}
      {/* Profile Completion Section */}
      <section className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${isPWA ? '-mt-8 sm:-mt-10' : '-mt-16'} relative z-30 mb-4`}>
        {/* Incomplete Profile Banner */}
        {session && (
          (() => {
            const isProfileComplete = 
              session.mobile && 
              session.gender && 
              session.address?.addressLine1 && 
              session.address?.city && 
              session.address?.state && 
              session.address?.pincode;
            
            if (!isProfileComplete) {
              return (
                <div className={`bg-amber-50 border-l-4 border-amber-400 ${isPWA ? 'p-3' : 'p-4'} mb-4 rounded-lg shadow-sm`}>
                  <div className="flex items-start">
                    <FaExclamationTriangle className={`text-amber-400 mr-3 ${isPWA ? 'text-lg' : 'text-xl'} mt-0.5 flex-shrink-0`} />
                    <div className="flex-1">
                      <h3 className={`text-amber-800 font-semibold ${isPWA ? 'text-sm mb-1' : 'mb-1'}`}>
                        Complete Your Profile
                      </h3>
                      <p className="text-amber-700 text-sm mb-3">
                        Please complete your personal details (Mobile, Gender, and Address) so trainers can understand you better and provide personalized guidance.
                      </p>
                      <button
                        onClick={() => navigate('/student/profile')}
                        className={`inline-flex items-center gap-2 ${isPWA ? 'px-3 py-1.5 text-xs' : 'px-4 py-2 text-sm'} bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-all shadow-md font-medium`}
                      >
                        Update Profile
                        <FaChevronRight className="text-xs" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            }
            return null;
          })()
        )}

        {/* Pending Approval Banner - Compact in PWA */}
        {((session?.isTrainer && session?.trainerStatus === 'inactive') || session?.hasPendingApplication) && (
          <div className={`bg-teal-50 border-l-4 border-teal-400 ${isPWA ? 'p-3' : 'p-4'} mb-4 rounded-lg`}>
            <div className="flex items-center">
              <FaClock className={`text-teal-400 mr-3 ${isPWA ? 'text-lg' : 'text-xl'}`} />
              <div>
                <h3 className={`text-teal-800 font-semibold ${isPWA ? 'text-sm' : ''}`}>{isPWA ? 'Application Pending' : 'Business Partner Application Pending'}</h3>
                {!isPWA && <p className="text-teal-700 text-sm">Your application is under review. You'll be notified via email once approved.</p>}
              </div>
            </div>
          </div>
        )}

        {/* Rejection Banner - Compact in PWA */}
        {session?.hasRejectedApplication && (
          <div className={`bg-red-50 border-l-4 border-red-400 ${isPWA ? 'p-3' : 'p-4'} mb-4 rounded-lg`}>
            <div className="flex items-start">
              <FaTimes className={`text-red-400 mr-3 ${isPWA ? 'text-lg' : 'text-xl'} mt-0.5 flex-shrink-0`} />
              <div className="flex-1">
                <h3 className={`text-red-800 font-semibold ${isPWA ? 'text-sm mb-1' : 'mb-2'}`}>{isPWA ? 'Application Rejected' : 'Business Partner Application Rejected'}</h3>
                {!isPWA && (
                  <>
                    <p className="text-red-700 text-sm mb-2">
                      Unfortunately, your business partner application has been rejected.
                    </p>
                    {session?.rejectionReason && (
                      <div className="mt-3 p-3 bg-red-100 rounded-lg border border-red-200">
                        <p className="text-red-800 font-medium text-sm mb-1">Reason for Rejection:</p>
                        <p className="text-red-700 text-sm">{session.rejectionReason}</p>
                      </div>
                    )}
                    <p className="text-red-600 text-xs mt-3 mb-3">
                      If you have any questions or would like to discuss this further, please contact our support team.
                    </p>
                  </>
                )}
                <button
                  onClick={() => setShowBecomePartner(true)}
                  className={`mt-2 ${isPWA ? 'px-3 py-1.5 text-xs' : 'px-4 py-2 text-sm'} bg-gradient-to-r from-teal-600 to-emerald-600 text-white rounded-lg hover:from-teal-700 hover:to-emerald-700 transition-all shadow-md hover:shadow-lg font-medium`}
                >
                  {isPWA ? 'Reapply' : 'Reapply for Business Partner'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Toggle Dashboard View - Show if approved business partner - Compact in PWA */}
        {session?.isTrainer && session?.trainerStatus === 'active' && (
          <div className={`${isPWA ? 'bg-gradient-to-r from-teal-50 via-blue-50 to-indigo-50 border-2 border-teal-200 shadow-lg' : 'bg-white shadow-sm'} rounded-xl ${isPWA ? 'p-3' : 'p-4'} mb-4 relative overflow-hidden`}>
            {isPWA && (
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-teal-200/30 to-blue-200/20 rounded-full blur-2xl"></div>
            )}
            <div className={`flex items-center justify-between ${isPWA ? 'gap-2' : ''} relative z-10`}>
              <div className="flex items-center">
                <div className={`${isPWA ? 'bg-gradient-to-br from-teal-500 to-blue-600 p-2 rounded-lg shadow-md' : ''}`}>
                  <FaBriefcase className={`${isPWA ? 'text-white' : 'text-teal-600'} ${isPWA ? 'text-base' : 'mr-3 text-xl'}`} />
                </div>
                <div className={`${isPWA ? 'ml-2' : ''}`}>
                  <h3 className={`${isPWA ? 'text-teal-800' : 'text-gray-800'} font-bold ${isPWA ? 'text-sm' : ''}`}>Member Dashboard</h3>
                  {!isPWA && <p className="text-gray-600 text-sm">Switch between member and professional views</p>}
                  {isPWA && (
                    <p className="text-xs text-teal-600/70 mt-0.5 font-medium">Your personal journey</p>
                  )}
                </div>
              </div>
              <button
                onClick={() => setShowSwitchConfirm(true)}
                title="Switch from (Member to professional) dashboard"
                className={`flex items-center gap-2 ${isPWA ? 'px-3 py-1.5 text-xs sm:text-sm' : 'px-4 py-2'} ${isPWA ? 'bg-gradient-to-r from-slate-600 to-slate-700 hover:from-slate-500 hover:to-slate-600 shadow-md border border-slate-500/50' : 'bg-gradient-to-r from-slate-600 via-blue-700 to-slate-700 hover:from-slate-500 hover:via-blue-600 hover:to-slate-600'} text-white rounded-lg transition-all shadow-md hover:shadow-lg ${!isPWA ? 'border border-cyan-300/50' : ''}`}
              >
                {dashboardView === 'member' ? (
                  <>
                    <span>Switch to Professional</span>
                    <FaToggleOff className={isPWA ? 'text-base flex-shrink-0' : 'text-xl'} />
                  </>
                ) : (
                  <>
                    <span>{isPWA ? 'Member Dashboard' : 'Switch to Member'}</span>
                    <FaToggleOn className={isPWA ? 'text-base flex-shrink-0' : 'text-xl'} />
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Become Business Partner Card - Show if not a business partner, no pending application, and not rejected - Compact in PWA */}
        {!session?.isTrainer && !session?.hasPendingApplication && !session?.hasRejectedApplication && (
          <div className={`bg-gradient-to-r from-teal-50 to-emerald-50 border-2 border-teal-200 rounded-xl ${isPWA ? 'p-3' : 'p-6'} mb-4 shadow-xl relative z-40`}>
            <div className={`flex items-center ${isPWA ? 'flex-row gap-2 sm:gap-3' : 'justify-between'}`}>
              <div className={`flex items-center ${isPWA ? 'flex-1 min-w-0' : ''}`}>
                <div className={`bg-teal-100 ${isPWA ? 'p-1.5' : 'p-3'} rounded-full ${isPWA ? 'mr-2 sm:mr-3' : 'mr-4'} flex-shrink-0`}>
                  <FaBriefcase className={`text-teal-600 ${isPWA ? 'text-base sm:text-lg' : 'text-2xl'}`} />
                </div>
                <div className="min-w-0 flex-1">
                  <h3
                    className={`text-gray-800 font-bold ${isPWA ? 'text-sm sm:text-base' : 'text-lg'} ${isPWA ? '' : 'mb-1'}`}
                    title={isPWA ? "Register as a professional service provider and start offering your expertise" : undefined}
                  >
                    Become a Business Partner
                  </h3>
                  {!isPWA && <p className="text-gray-600 text-sm">Register as a professional service provider and start offering your expertise</p>}
                </div>
              </div>
              <button
                onClick={() => setShowBecomePartner(true)}
                title={isPWA ? "Register as a professional service provider and start offering your expertise" : undefined}
                className={`${isPWA ? 'px-3 py-1.5 text-xs sm:text-sm flex-shrink-0' : 'px-6 py-3'} bg-gradient-to-r from-teal-600 to-emerald-600 text-white rounded-lg hover:from-teal-700 hover:to-emerald-700 transition-all shadow-md hover:shadow-lg`}
              >
                Register Now
              </button>
            </div>
          </div>
        )}

        {/* Become Business Partner Modal */}
        {showBecomePartner && (
          <BecomeBusinessPartner
            onClose={() => setShowBecomePartner(false)}
            onSuccess={async () => {
              try {
                // Refresh session to get updated trainer info
                const response = await HttpInterceptor.get('/auth/session');
                if (response.data && setSession) {
                  setSession(response.data);
                }
              } catch (error) {
                console.error('Error refreshing session:', error);
                // Don't show error toast to user, just log it
              }
              // Always close the modal after session refresh (success or failure)
              setShowBecomePartner(false);
            }}
          />
        )}
      </section>

      {/* Compact Setup Guide - Hidden in PWA mode */}
      {!isPWA && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative mb-6">
          <div className="bg-white rounded-xl shadow-sm p-6 transition-all duration-300 hover:shadow-md">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Your Learning Journey</h2>
            <div className="flex items-center justify-between">
              {/* Enroll in Programs */}
              <div className="flex flex-col items-center relative flex-1">
                {learningJourneyStatus.hasEnrolledPrograms ? (
                  <div className="flex items-center justify-center w-10 h-10 rounded-full mb-2 bg-green-100 text-green-600">
                    <FaCheckCircle className="text-lg" />
                  </div>
                ) : (
                  <Link
                    to="/student/programs"
                    className="flex items-center justify-center w-10 h-10 rounded-full mb-2 bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-700 transition-colors"
                  >
                    <FaClipboardList className="text-lg" />
                  </Link>
                )}
                <div className="text-center">
                  <h3 className={`text-sm font-medium ${learningJourneyStatus.hasEnrolledPrograms ? 'text-green-600' : 'text-gray-700'}`}>
                    Enroll in Programs
                  </h3>
                  <p className="text-xs text-gray-500 mt-1 hidden md:block">Choose from available training programs</p>
                </div>
              </div>
              {/* Connector line */}
              <div className={`h-1 w-16 ${learningJourneyStatus.hasEnrolledPrograms ? 'bg-green-500' : 'bg-gray-200'}`}></div>
              {/* Attend Classes */}
              <div className="flex flex-col items-center relative flex-1">
                {learningJourneyStatus.hasAttendedClasses ? (
                  <div className="flex items-center justify-center w-10 h-10 rounded-full mb-2 bg-green-100 text-green-600">
                    <FaCheckCircle className="text-lg" />
                  </div>
                ) : (
                  <Link
                    to="/student/classes"
                    className="flex items-center justify-center w-10 h-10 rounded-full mb-2 bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-700 transition-colors"
                  >
                    <FaCalendarAlt className="text-lg" />
                  </Link>
                )}
                <div className="text-center">
                  <h3 className={`text-sm font-medium ${learningJourneyStatus.hasAttendedClasses ? 'text-green-600' : 'text-gray-700'}`}>
                    Attend Classes
                  </h3>
                  <p className="text-xs text-gray-500 mt-1 hidden md:block">Join scheduled sessions regularly</p>
                </div>
              </div>
              {/* Connector line */}
              <div className={`h-1 w-16 ${learningJourneyStatus.hasEnrolledPrograms && learningJourneyStatus.hasAttendedClasses ? 'bg-green-500' : 'bg-gray-200'}`}></div>
              {/* Track Progress */}
              <div className="flex flex-col items-center relative flex-1">
                {learningJourneyStatus.hasTrackedProgress ? (
                  <div className="flex items-center justify-center w-10 h-10 rounded-full mb-2 bg-green-100 text-green-600">
                    <FaCheckCircle className="text-lg" />
                  </div>
                ) : (
                  <Link
                    to="/student/profile"
                    className="flex items-center justify-center w-10 h-10 rounded-full mb-2 bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-700 transition-colors"
                  >
                    <FaChartLine className="text-lg" />
                  </Link>
                )}
                <div className="text-center">
                  <h3 className={`text-sm font-medium ${learningJourneyStatus.hasTrackedProgress ? 'text-green-600' : 'text-gray-700'}`}>
                    Track Progress
                  </h3>
                  <p className="text-xs text-gray-500 mt-1 hidden md:block">Monitor your achievements</p>
                </div>
              </div>
              {/* Connector line */}
              <div className={`h-1 w-16 ${learningJourneyStatus.hasEnrolledPrograms && learningJourneyStatus.hasAttendedClasses && learningJourneyStatus.hasTrackedProgress ? 'bg-green-500' : 'bg-gray-200'}`}></div>
              {/* Achieve Goals */}
              <div className="flex flex-col items-center relative flex-1">
                {learningJourneyStatus.hasAchievedGoals ? (
                  <div className="flex items-center justify-center w-10 h-10 rounded-full mb-2 bg-green-100 text-green-600">
                    <FaCheckCircle className="text-lg" />
                  </div>
                ) : (
                  <div className="flex items-center justify-center w-10 h-10 rounded-full mb-2 bg-gray-100 text-gray-600">
                    <FaMedal className="text-lg" />
                  </div>
                )}
                <div className="text-center">
                  <h3 className={`text-sm font-medium ${learningJourneyStatus.hasAchievedGoals ? 'text-green-600' : 'text-gray-700'}`}>
                    Achieve Goals
                  </h3>
                  <p className="text-xs text-gray-500 mt-1 hidden md:block">Reach your fitness milestones</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Stats Overview */}
      <section className={`${isPWA ? 'py-6 sm:py-8 pb-10 sm:pb-12' : 'py-12'} ${isPWA ? 'bg-gradient-to-b from-teal-50/40 via-blue-50/30 to-white' : 'bg-white'} relative`}>
        {isPWA && (
          <>
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-teal-400/60 to-transparent"></div>
            <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-teal-200/40 to-transparent"></div>
          </>
        )}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`flex items-center gap-3 ${isPWA ? 'mb-6 sm:mb-8 px-2' : 'mb-8 px-4'}`}>
            {isPWA && (
              <div className="relative">
                <div className="absolute left-0 w-1.5 h-8 bg-gradient-to-b from-teal-400/40 via-blue-400/40 to-indigo-400/40 rounded-full blur-sm"></div>
                <div className="relative w-1.5 h-8 bg-gradient-to-b from-teal-500 via-blue-500 to-indigo-500 rounded-full shadow-md"></div>
              </div>
            )}
            <div>
              <h2 className={`${isPWA ? 'text-xl sm:text-2xl' : 'text-2xl'} font-bold ${isPWA ? 'text-teal-900' : 'text-gray-800'} ${isPWA ? 'drop-shadow-sm' : ''}`}>Overview</h2>
              {isPWA && (
                <p className="text-xs text-teal-600/70 mt-0.5 font-medium">Your progress at a glance</p>
              )}
            </div>
          </div>
          <div className={`grid ${isPWA ? 'grid-cols-2' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4'} ${isPWA ? 'gap-3' : 'gap-4 sm:gap-6'}`}>
            <StatCard
              title="Classes Attended"
              value={stats.classesAttended}
              icon={<FaCalendarAlt />}
              color="text-blue-600"
              bgColor="bg-blue-50"
              progress={stats.progressAchieved}
            />
            <StatCard
              title="Courses Enrolled"
              value={stats.coursesEnrolled}
              icon={<FaClipboardList />}
              color="text-green-600"
              bgColor="bg-green-50"
            />
            <StatCard
              title="Progress Achieved"
              value={`${stats.progressAchieved}%`}
              icon={<FaChartLine />}
              color="text-purple-600"
              bgColor="bg-purple-50"
              progress={stats.progressAchieved}
            />
            <StatCard
              title="Pending Payments"
              value={stats.pendingPayments}
              icon={<FaMoneyBillWave />}
              color="text-teal-600"
              bgColor="bg-teal-50"
            />
          </div>
        </div>
      </section>

      {/* Navigation / Quick Actions */}
      <section className={`${isPWA ? 'pt-10 sm:pt-12 pb-6 sm:py-8' : 'py-12'} ${isPWA ? 'bg-gradient-to-b from-white via-teal-50/30 to-white' : 'bg-gray-50'} relative`}>
        {isPWA && (
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-teal-400/60 to-transparent"></div>
        )}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`flex items-center gap-3 ${isPWA ? 'mb-6 sm:mb-8' : 'mb-8'}`}>
            {isPWA && (
              <div className="relative">
                <div className="absolute left-0 w-1.5 h-7 bg-gradient-to-b from-teal-400/40 via-blue-400/40 to-indigo-400/40 rounded-full blur-sm"></div>
                <div className="relative w-1.5 h-7 bg-gradient-to-b from-teal-500 via-blue-500 to-indigo-500 rounded-full shadow-md"></div>
              </div>
            )}
            <div>
              <h2 className={`${isPWA ? 'text-xl sm:text-2xl' : 'text-2xl'} font-bold ${isPWA ? 'text-teal-900 drop-shadow-sm' : 'text-gray-800'}`}>Quick Actions</h2>
              {isPWA && (
                <p className="text-xs text-teal-600/70 mt-0.5 font-medium">Access key features</p>
              )}
            </div>
          </div>
          {isPWA ? (
            <div className="bg-gradient-to-br from-white to-teal-50/40 via-blue-50/20 rounded-2xl shadow-2xl border-2 border-teal-200/50 hover:shadow-3xl transition-all duration-300 p-4 sm:p-5 relative overflow-hidden">
              {/* Decorative overlays */}
              <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-teal-100/20 to-blue-100/10 rounded-full blur-3xl"></div>
              <div className="absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-br from-blue-100/20 to-indigo-100/10 rounded-full blur-3xl"></div>

              <div className="flex flex-nowrap gap-3 sm:gap-4 overflow-x-auto overflow-y-visible pb-2 scrollbar-hide -mx-2 px-2 sm:mx-0 sm:px-0 pt-2 relative z-10">
                {navigationItems.map((item, index) => (
                  <Link
                    key={index}
                    to={item.link}
                    className={`bg-gradient-to-br from-white to-teal-50/30 ${item.textColor || 'text-gray-700'} p-4 sm:p-5 rounded-2xl shadow-xl transition-all duration-300 hover:shadow-2xl hover:-translate-y-2 border-2 border-teal-100/50 hover:border-teal-300/50 active:scale-95 flex flex-col items-center text-center group relative overflow-visible flex-shrink-0 min-w-[110px] sm:min-w-[130px]`}
                  >
                    {/* Decorative gradient overlay - behind content */}
                    <div className="absolute top-0 right-0 w-16 h-16 bg-gradient-to-br from-teal-200/20 to-blue-200/10 rounded-full blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-0"></div>

                    {/* Top accent line on hover */}
                    <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-teal-300/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

                    <div className={`${item.iconBg || 'bg-teal-100'} p-3 sm:p-3.5 rounded-xl mb-2 group-hover:scale-110 transition-transform duration-300 shadow-md ring-2 ring-teal-100/50 relative z-10`}>
                      <div className="text-2xl sm:text-3xl">
                        {item.icon}
                      </div>
                    </div>
                    <h3 className="text-[10px] sm:text-xs font-semibold relative z-10 leading-tight">{item.title}</h3>
                  </Link>
                ))}
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {quickActions.map((action, index) => (
                <Link
                  key={index}
                  to={action.link}
                  className={`${action.bgColor || 'bg-white'} ${action.textColor || 'text-gray-700'} p-6 rounded-xl shadow-sm transition-all duration-200 hover:shadow-md border border-gray-100`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold mb-2">{action.title}</h3>
                      {action.description && (
                        <p className="text-sm text-gray-600">{action.description}</p>
                      )}
                    </div>
                    <div className="text-2xl">
                      {action.icon}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Recent Activities & Wellness Tips */}
      <section className={`${isPWA ? 'py-6 sm:py-8' : 'py-12'} ${isPWA ? 'bg-gradient-to-b from-white via-amber-50/30 to-white' : 'bg-white'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`grid grid-cols-1 ${isPWA ? '' : 'lg:grid-cols-3'} ${isPWA ? 'gap-4 sm:gap-6' : 'gap-8'}`}>
            {/* Recent Activities */}
            <div className={isPWA ? 'mb-6' : 'lg:col-span-2'}>
              <div className={`${isPWA ? 'bg-gradient-to-br from-white via-teal-50/30 to-white rounded-2xl shadow-xl border-2 border-teal-200/50 p-5 sm:p-6 transition-all duration-300 hover:shadow-2xl relative overflow-hidden' : 'bg-white rounded-xl shadow-sm p-6 border border-gray-100'}`}>
                {isPWA && (
                  <>
                    <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-teal-400/60 to-transparent"></div>
                    <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-teal-100/20 to-blue-100/10 rounded-full blur-2xl"></div>
                  </>
                )}
                <div className={`flex items-center justify-between ${isPWA ? 'mb-5 sm:mb-6' : 'mb-6'} relative z-10`}>
                  <div className="flex items-center gap-3">
                    {isPWA && (
                      <div className="relative">
                        <div className="absolute left-0 w-1.5 h-7 bg-gradient-to-b from-teal-400/40 via-blue-400/40 to-indigo-400/40 rounded-full blur-sm"></div>
                        <div className="relative w-1.5 h-7 bg-gradient-to-b from-teal-500 via-blue-500 to-indigo-500 rounded-full shadow-md"></div>
                      </div>
                    )}
                    <div>
                      <h2 className={`${isPWA ? 'text-xl sm:text-2xl' : 'text-xl'} font-bold ${isPWA ? 'text-teal-900 drop-shadow-sm' : 'text-gray-800'}`}>{isPWA ? 'Activities' : 'Recent Activities'}</h2>
                      {isPWA && (
                        <p className="text-xs text-teal-600/70 mt-0.5 font-medium">Your recent actions</p>
                      )}
                    </div>
                  </div>
                  <Link to="/student/myclasses" className={`${isPWA ? 'text-xs sm:text-sm' : 'text-sm'} ${isPWA ? 'text-teal-700 hover:text-teal-800 bg-teal-50 px-3 py-1.5 rounded-lg border border-teal-200/50 hover:bg-teal-100 transition-colors' : 'text-blue-600 hover:text-blue-800'} font-medium`}>
                    {isPWA ? 'All' : 'View All'} <FaChevronRight className={`inline ${isPWA ? 'ml-0.5 text-[10px]' : 'ml-1'}`} />
                  </Link>
                </div>
                {/* Loading State */}
                {isLoadingActivities && (
                  <div className={`text-center ${isPWA ? 'py-4' : 'py-8'}`}>
                    <div className={`animate-spin rounded-full ${isPWA ? 'h-6 w-6' : 'h-8 w-8'} border-t-2 border-b-2 border-blue-500 mx-auto`}></div>
                    <p className={`${isPWA ? 'mt-1 text-xs' : 'mt-2 text-sm'} text-gray-600`}>Loading activities...</p>
                  </div>
                )}

                {/* Error State */}
                {activitiesError && !isLoadingActivities && (
                  <div className={`text-center ${isPWA ? 'py-4' : 'py-8'}`}>
                    <FaExclamationTriangle className={`${isPWA ? 'text-2xl' : 'text-3xl'} text-gray-400 mx-auto ${isPWA ? 'mb-1' : 'mb-2'}`} />
                    <p className={`${isPWA ? 'text-xs' : 'text-sm'} text-gray-500`}>{activitiesError}</p>
                  </div>
                )}

                {/* Empty State */}
                {!activitiesError && !isLoadingActivities && activities.length === 0 && (
                  <div className={`text-center ${isPWA ? 'py-4' : 'py-8'}`}>
                    <FaInbox className={`${isPWA ? 'text-2xl sm:text-3xl' : 'text-4xl'} text-gray-400 mx-auto ${isPWA ? 'mb-2' : 'mb-3'}`} />
                    <h3 className={`${isPWA ? 'text-xs sm:text-sm' : 'text-sm'} font-medium text-gray-900 ${isPWA ? 'mb-0.5' : 'mb-1'}`}>No Recent Activities</h3>
                    <p className={`${isPWA ? 'text-[10px] sm:text-xs' : 'text-xs'} text-gray-500 ${isPWA ? 'mb-2' : 'mb-4'}`}>Your recent activities will appear here</p>
                    <Link
                      to="/student/myclasses"
                      className={`inline-block ${isPWA ? 'text-xs' : 'text-sm'} text-blue-600 hover:text-blue-800 font-medium`}
                    >
                      Browse Classes →
                    </Link>
                  </div>
                )}

                {/* Activities List */}
                {!activitiesError && !isLoadingActivities && activities.length > 0 && (
                  <div className={`${isPWA ? 'space-y-3 sm:space-y-4' : 'space-y-4'} relative z-10`}>
                    {activities.map((activity, index) => (
                      <div key={index} className={isPWA ? `p-4 bg-gradient-to-br from-white to-teal-50/20 shadow-md border-2 border-teal-100/50 rounded-xl hover:shadow-lg hover:border-teal-200/50 transition-all group relative overflow-hidden` : `flex items-center p-4 bg-gray-50 rounded-lg border border-gray-100`}>
                        {isPWA && (
                          <div className="absolute top-0 right-0 w-16 h-16 bg-gradient-to-br from-teal-100/20 to-blue-100/10 rounded-full blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                        )}
                        {isPWA ? (
                          <div className="flex items-start gap-3 relative z-10">
                            <div className={`rounded-full p-2.5 flex-shrink-0 bg-gradient-to-br from-green-100 to-green-200 text-green-700 shadow-sm ring-1 ring-green-200/50`}>
                              <div className={`${isPWA ? 'text-base sm:text-lg' : 'text-xl'}`}>
                                {activity.icon}
                              </div>
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className={`${isPWA ? 'text-xs sm:text-sm' : 'text-sm'} font-medium text-gray-900 ${isPWA ? 'mb-1' : 'mb-1'}`}>
                                {activity.type === 'class' ? activity.name :
                                  activity.type === 'payment' ? `Payment of ₹${activity.amount}` :
                                    activity.title || activity.milestone}
                              </p>
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-1.5">
                                  <FaCalendarAlt className="text-[10px] text-gray-400" />
                                  <p className={`${isPWA ? 'text-[10px] sm:text-xs' : 'text-xs'} text-gray-500`}>{activity.time}</p>
                                </div>
                                {activity.points > 0 && (
                                  <span className={`${isPWA ? 'text-[9px] px-1.5 py-0.5' : 'text-[10px] px-2 py-0.5'} font-medium text-green-800 bg-gradient-to-r from-green-100 to-green-200 border border-green-300/50 rounded-full shadow-sm`}>
                                    +{activity.points} pts
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        ) : (
                          <>
                            <div className="mr-4 text-xl flex-shrink-0">
                              {activity.icon}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between">
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium text-gray-900 truncate">
                                    {activity.type === 'class' ? activity.name :
                                      activity.type === 'payment' ? `Payment of ₹${activity.amount}` :
                                        activity.title || activity.milestone}
                                  </p>
                                  <p className="text-sm text-gray-500 truncate">{activity.time}</p>
                                </div>
                              </div>
                              {activity.points > 0 && (
                                <div className="text-right mt-1">
                                  <span className="text-xs px-2 py-1 font-medium text-green-600 bg-green-100 rounded-full">
                                    +{activity.points} pts
                                  </span>
                                </div>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Wellness Tips */}
            <div>
              <div className={`${isPWA ? 'bg-gradient-to-br from-white via-teal-50/30 to-white rounded-2xl shadow-xl border-2 border-teal-200/50 p-5 sm:p-6 transition-all duration-300 hover:shadow-2xl relative overflow-hidden' : 'bg-white rounded-xl shadow-sm p-6 border border-gray-100'}`}>
                {isPWA && (
                  <>
                    <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-teal-400/60 to-transparent"></div>
                    <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-teal-100/20 to-blue-100/10 rounded-full blur-2xl"></div>
                  </>
                )}
                <div className={`flex items-center gap-3 ${isPWA ? 'mb-5 sm:mb-6' : 'mb-6'} relative z-10`}>
                  {isPWA && (
                    <div className="relative">
                      <div className="absolute left-0 w-1.5 h-7 bg-gradient-to-b from-teal-400/40 via-blue-400/40 to-indigo-400/40 rounded-full blur-sm"></div>
                      <div className="relative w-1.5 h-7 bg-gradient-to-b from-teal-500 via-blue-500 to-indigo-500 rounded-full shadow-md"></div>
                    </div>
                  )}
                  <div>
                    <h2 className={`${isPWA ? 'text-xl sm:text-2xl' : 'text-xl'} font-bold ${isPWA ? 'text-teal-900 drop-shadow-sm' : 'text-gray-800'}`}>Wellness Tips</h2>
                    {isPWA && (
                      <p className="text-xs text-teal-600/70 mt-0.5 font-medium">Daily wellness insights</p>
                    )}
                  </div>
                </div>
                <div className={`${isPWA ? 'space-y-3' : 'space-y-4'} relative z-10`}>
                  {wellnessTips.map((tip, index) => (
                    <div key={index} className={`${isPWA ? 'p-4 bg-gradient-to-br from-white to-teal-50/20 shadow-md border-2 border-teal-100/50 rounded-xl hover:shadow-lg hover:border-teal-200/50 transition-all group relative overflow-hidden' : `${tip.color} p-4 rounded-lg border border-gray-100`}`}>
                      {isPWA && (
                        <div className="absolute top-0 right-0 w-16 h-16 bg-gradient-to-br from-teal-100/20 to-blue-100/10 rounded-full blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      )}
                      <div className="flex items-start relative z-10">
                        <div className={`${isPWA ? 'mr-3 p-2.5 rounded-xl bg-gradient-to-br from-teal-100 to-blue-100 text-teal-700 shadow-sm ring-1 ring-teal-200/50' : ''} ${isPWA ? 'text-base sm:text-lg' : 'mr-3 text-xl'} ${!isPWA ? 'mt-0.5' : ''} flex-shrink-0`}>
                          {tip.icon}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className={`${isPWA ? 'text-sm font-semibold' : 'font-medium'} text-gray-900 ${isPWA ? 'mb-1' : 'mb-1'}`}>{tip.title}</h3>
                          <p className={`${isPWA ? 'text-xs' : 'text-sm'} text-gray-600`}>{tip.description}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Dashboard Switch Confirmation Modal */}
      {showSwitchConfirm && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full animate-slide-up">
            {/* Header */}
            <div className="bg-gradient-to-r from-teal-600 via-blue-600 to-indigo-600 text-white p-6 rounded-t-xl">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                  <FaBriefcase className="text-2xl" />
                </div>
                <div>
                  <h2 className="text-xl font-bold">Switch Dashboard View</h2>
                  <p className="text-white/90 text-sm">Confirm your dashboard switch</p>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 space-y-4">
              <div className="flex items-start gap-3">
                <FaExclamationTriangle className="text-teal-500 text-xl mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-gray-700 font-medium mb-2">Switch to Professional Dashboard?</p>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    You're about to switch from your <strong>Member Dashboard</strong> to your <strong>Professional Dashboard</strong>.
                    This will take you to your business view where you can manage students, programs, fees, and access professional features.
                  </p>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3 p-6 border-t border-gray-200">
              <button
                onClick={() => setShowSwitchConfirm(false)}
                className="flex-1 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowSwitchConfirm(false);
                  if (dashboardView === 'member') {
                    setDashboardView('professional');
                    navigate('/trainer/home');
                  } else {
                    setDashboardView('member');
                    // Already on member dashboard
                  }
                }}
                className="flex-1 px-4 py-2.5 bg-gradient-to-r from-teal-600 via-blue-600 to-indigo-600 hover:from-teal-700 hover:via-blue-700 hover:to-indigo-700 text-white rounded-lg font-medium transition-all shadow-md hover:shadow-lg"
              >
                Switch to Professional
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ title, value, icon, trend, color, bgColor, progress }) {
  const [animatedValue, setAnimatedValue] = useState(0);
  const [isPWA, setIsPWA] = useState(false);

  useEffect(() => {
    const checkPWA = () => {
      const standalone = window.matchMedia('(display-mode: standalone)').matches ||
        window.navigator.standalone === true;
      setIsPWA(standalone);
    };
    checkPWA();
    const mediaQuery = window.matchMedia('(display-mode: standalone)');
    const handleChange = () => checkPWA();
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);


  useEffect(() => {
    let timer;
    const animate = () => {
      const target = typeof value === 'number' ? value : parseInt(value);
      if (isNaN(target)) {
        setAnimatedValue(value);
        return;
      }

      const increment = Math.max(1, target / 50);
      let current = 0;

      timer = setInterval(() => {
        current += increment;
        if (current >= target) {
          setAnimatedValue(target);
          clearInterval(timer);
        } else {
          setAnimatedValue(Math.floor(current));
        }
      }, 20);
    };

    animate();

    return () => {
      if (timer) clearInterval(timer);
    };
  }, [value]);

  if (isPWA) {
    // App-like design for PWA mode
    const getGradientClass = (bgColor) => {
      if (bgColor.includes('blue')) return 'from-blue-100/40 to-blue-200/20';
      if (bgColor.includes('green')) return 'from-green-100/40 to-green-200/20';
      if (bgColor.includes('purple')) return 'from-purple-100/40 to-purple-200/20';
      if (bgColor.includes('orange')) return 'from-orange-100/40 to-orange-200/20';
      return 'from-gray-100/40 to-gray-200/20';
    };

    const getProgressGradient = (color) => {
      if (color.includes('blue')) return 'bg-gradient-to-r from-blue-500 to-blue-600';
      if (color.includes('green')) return 'bg-gradient-to-r from-green-500 to-green-600';
      if (color.includes('purple')) return 'bg-gradient-to-r from-purple-500 to-purple-600';
      if (color.includes('orange')) return 'bg-gradient-to-r from-orange-500 to-orange-600';
      return 'bg-gradient-to-r from-gray-500 to-gray-600';
    };

    return (
      <div className={`bg-gradient-to-br from-white to-teal-50/20 border-2 border-teal-200/50 p-4 sm:p-5 rounded-2xl shadow-xl hover:shadow-2xl relative overflow-hidden transition-all duration-300`}>
        {/* Top accent line */}
        <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-teal-300/60 to-transparent"></div>

        {/* Decorative gradient overlay */}
        <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-teal-100/20 to-blue-100/10 rounded-full blur-2xl"></div>

        <div className="relative z-10">
          <div className="flex items-start justify-between mb-3">
            <div className="flex-1 min-w-0">
              <p className="text-[11px] sm:text-xs font-medium text-gray-600 mb-1 uppercase tracking-wide">{title}</p>
              <p className={`text-2xl sm:text-3xl font-bold text-teal-800 leading-tight drop-shadow-sm`}>
                {typeof value === 'number' ? animatedValue : value}
              </p>
            </div>
            <div className={`bg-gradient-to-br from-teal-50 to-blue-50 ${color} p-3 sm:p-3.5 rounded-xl shadow-md ring-1 ring-teal-100/50 flex-shrink-0 ml-2`}>
              <div className="text-lg sm:text-xl">
                {icon}
              </div>
            </div>
          </div>
          {progress !== undefined && progress !== null && (
            <div className="mt-3">
              <div className="w-full bg-white/20 backdrop-blur-sm border border-teal-200/30 rounded-full h-2 overflow-hidden shadow-inner">
                <div
                  className="bg-gradient-to-r from-teal-500 via-blue-500 to-indigo-500 h-2 rounded-full transition-all duration-1000 shadow-sm"
                  style={{ width: `${Math.min(progress, 100)}%` }}
                ></div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Original design for non-PWA mode
  return (
    <div className={`${bgColor} p-3 sm:p-4 md:p-6 rounded-xl shadow-sm border border-gray-100 transition-all duration-300 hover:shadow-md`}>
      <div className="flex items-center justify-between">
        <div className="flex-1 min-w-0">
          <p className="text-xs sm:text-sm font-medium text-gray-600 mb-0.5 sm:mb-1 truncate">{title}</p>
          <p className={`text-lg sm:text-xl md:text-2xl font-bold ${color} truncate`}>
            {typeof value === 'number' ? animatedValue : value}
          </p>
          {trend && (
            <p className="text-[10px] sm:text-xs text-gray-500 mt-0.5 sm:mt-1 truncate">{trend}</p>
          )}
        </div>
        <div className={`text-xl sm:text-2xl md:text-3xl ${color} flex-shrink-0 ml-2`}>
          {icon}
        </div>
      </div>
      {progress && (
        <div className="mt-2 sm:mt-3">
          <div className="w-full bg-gray-200 rounded-full h-1 sm:h-1.5 md:h-2">
            <div
              className="bg-blue-600 rounded-full transition-all duration-1000 h-1 sm:h-1.5 md:h-2"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
        </div>
      )}
    </div>
  );
}

export default HomeStud;